import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Shield, Award, CheckCircle, ArrowRight, Users, BookOpen, Zap, Globe, Dumbbell, Cpu } from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxrZXlub3RlJTIwc3BlYWtlciUyMHN0YWdlJTIwcHJlc2VudGF0aW9ufGVufDB8fHx8MTc4NjY3MDMzOHww&ixlib=rb-4.1.0&q=85&w=1200";
const VR_NEON = "https://images.unsplash.com/photo-1535223289827-42f1e9919769?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMFZSJTIwaGVhZHNldCUyMGltbWVyc2l2ZSUyMGxlYXJuaW5nJTIwZXhwZXJpZW5jZXxlbnwwfHx8fDE3ODY3MjkxNjJ8MA&ixlib=rb-4.1.0&q=85&w=800";

const tierIcons = [Shield, Award, Heart];
const tierColors = ['border-blue-500/30', 'border-purple-500/30', 'border-cta/50'];
const tierBg = ['bg-blue-500/10', 'bg-purple-500/10', 'bg-cta/10'];

const SponsorCard = ({ tier, index }) => {
  const Icon = tierIcons[index] || Shield;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      className={`p-6 md:p-8 rounded-2xl glass-light flex flex-col h-full border tech-bracket ${tierColors[index]}`}
      data-testid={`sponsor-card-${tier.id}`}
    >
      <div className={`w-14 h-14 rounded-xl ${tierBg[index]} flex items-center justify-center mb-4`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-2xl font-serif font-bold text-white mb-1">{tier.title}</h3>
      <p className="text-cta font-bold text-xl mb-2">{tier.price}</p>
      {tier.slogan && <p className="text-sm text-secondary italic mb-4">{tier.slogan}</p>}
      <p className="text-muted-foreground text-sm mb-6">{tier.description}</p>

      <div className="flex-1 mb-6">
        <h4 className="text-sm font-semibold text-white mb-3">Benefits:</h4>
        <ul className="space-y-2">
          {tier.benefits?.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <Link
        to="/contact"
        className="btn-primary w-full flex items-center justify-center gap-2 text-center"
        data-testid={`sponsor-cta-${tier.id}`}
      >
        Become a {tier.title} <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
};

const impactAreas = [
  { icon: BookOpen, label: "Immersive Education", desc: "VR/XR, AI, STEM, CTE, Virtual Field Trips" },
  { icon: Users, label: "Inclusive Learning", desc: "ADHD, Special Education, Underserved Communities" },
  { icon: Globe, label: "Cultural Education", desc: "Indigenous, Moorish, Black African History & Culture" },
  { icon: Dumbbell, label: "Esports & Athletics", desc: "Golden Gloves VR Boxing, Fitness, Physical Education" },
  { icon: Zap, label: "Life & Leadership", desc: "Leadership, Entrepreneurship, Career Exploration" },
  { icon: Cpu, label: "Emerging Technology", desc: "AI, Virtual Reality, Mixed Reality, Spatial Computing" },
];

export default function SponsorshipPage() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sponsorship-tiers`).then(res => { setTiers(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="sponsorship-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-testid="sponsorship-title">
                Sponsor The Matrix University
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Businesses, corporations, foundations, and community leaders can help expand access to emerging technology and innovative education by becoming a Matrix University Sponsor.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
                  <img src={HERO_IMAGE} alt="Conference presentation" className="w-full h-48 md:h-64 object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 mt-8">
                  <img src={VR_NEON} alt="VR experience" className="w-full h-48 md:h-64 object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1,2,3].map(i => <div key={i} className="p-8 rounded-2xl glass-light animate-pulse h-96" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {tiers.map((t, i) => <SponsorCard key={t.id} tier={t} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">Your Sponsorship Impact</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Your investment helps expand access to innovative educational experiences.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {impactAreas.map((area, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="p-5 rounded-xl glass-light text-center">
                <area.icon className="w-8 h-8 text-cta mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-1">{area.label}</h3>
                <p className="text-muted-foreground text-sm">{area.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          {/* Lower Donation Options */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-4xl mx-auto mb-16">
            <div className="text-center mb-8">
              <Heart className="w-10 h-10 text-cta mx-auto mb-3" />
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Support at Any Level</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">Every contribution helps expand access to immersive education and technology for learners everywhere.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
              {[
                { amount: "$250", value: 250 },
                { amount: "$500", value: 500 },
                { amount: "$1,000", value: 1000 },
                { amount: "$2,500", value: 2500 },
                { amount: "$5,000", value: 5000 },
              ].map((d, i) => (
                <Link
                  key={i}
                  to="/donate"
                  className="p-4 rounded-xl glass-light text-center card-hover tech-bracket"
                  data-testid={`donate-amount-${d.value}`}
                >
                  <p className="text-2xl font-bold text-cta">{d.amount}</p>
                  <p className="text-xs text-muted-foreground mt-1">Donate</p>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link to="/donate" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-semibold" data-testid="donate-custom-link">
                Or choose a custom amount <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Build With Us CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center p-8 rounded-2xl glass-light">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">Build With Us</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Your organization can help create the next generation of learning. Together, we can expand access to technology, immersive education, innovation, and career opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Request the Sponsorship Deck <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
